import { useMemo } from "react"
import type { GraphDatum } from "@/app/playbook/extrapolated-graph/types"
import {
  PLAN_KEYS,
  defaultGraphData,
} from "@/app/playbook/extrapolated-graph/graphConstants"
import { getCapabilityStageLabelForPlanValue } from "@/app/playbook/extrapolated-graph/capabilityThresholds"
import { useHudContext, useHudDates } from "../../hud/shared/playbookHUDInfra"

// Noun used before the capability line reaches the first threshold (the graph
// would say "Below Unreliable Agent" there, which reads badly after a count).
const PRE_THRESHOLD_NOUN = "AI Agents"

// The AI-agents legend noun, derived from the SAME data as the capabilities
// chart's current-point label: the extrapolated-graph value (shared series +
// the selected plan's series) at the HUD's animated date, mapped through the
// chart's own threshold-labeling rule and pluralized. Keeps the bottom
// section's "10K Automated coders working at 40x speed" in lockstep with the
// "Automated coder" label on the chart above it — unlike the compute-fit
// rdMultiple (deriveAgentDataFromCompute), which runs hotter than the plotted
// curve and would disagree near threshold crossings.
// Returns undefined when disabled so callers can pass it straight through.
export const useGraphMatchedAiAgentsNoun = (enabled: boolean): string | undefined => {
  const { selectedPlan } = useHudContext()
  const { animatedDate } = useHudDates()
  const animatedTime = animatedDate.getTime()
  return useMemo(() => {
    if (!enabled) return undefined
    // Mirror the graph's series selection (useExtrapolatedGraphRenderProps):
    // plan B draws its US spine when available.
    const planSeries: GraphDatum[] = selectedPlan === null
      ? []
      : selectedPlan === "B" && defaultGraphData.planBUs?.length
        ? defaultGraphData.planBUs
        : defaultGraphData[PLAN_KEYS[selectedPlan]] ?? []
    const value = interpolateGraphValueAtTime(
      [...defaultGraphData.shared, ...planSeries],
      animatedTime,
    )
    if (value === null) return PRE_THRESHOLD_NOUN
    const label = getCapabilityStageLabelForPlanValue(value, selectedPlan)
    if (label.startsWith("Below ")) return PRE_THRESHOLD_NOUN
    return `${label}s`
  }, [enabled, animatedTime, selectedPlan])
}

// Linear interpolation of the graph series' value at a timestamp, clamping to
// the first/last point outside the data range. Threshold labels only care
// about which side of a threshold the value lands on, so linear (rather than
// the renderer's log-ish screen-space blend) is accurate enough.
const interpolateGraphValueAtTime = (
  series: GraphDatum[],
  time: number,
): number | null => {
  if (series.length === 0) return null
  const points = series
    .map((datum) => ({ time: Date.parse(datum.date), value: datum.value }))
    .filter((point) => isFinite(point.time))
    .sort((a, b) => a.time - b.time)
  if (points.length === 0) return null
  if (time <= points[0].time) return points[0].value
  for (let i = 1; i < points.length; i++) {
    if (time <= points[i].time) {
      const prev = points[i - 1]
      const next = points[i]
      const span = next.time - prev.time
      const fraction = span > 0 ? (time - prev.time) / span : 1
      return prev.value + (next.value - prev.value) * fraction
    }
  }
  return points[points.length - 1].value
}
